import {
    Box,
    Button,
    capitalize,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router";
import {GridColDef, GridRowParams} from "@mui/x-data-grid";
import {
    DataLoader,
    DataRequest,
    DataRequestState,
    useDataRequest,
} from "../data";
import {useT} from "../../i18n";
import SearchField from "../SearchField";
import {useGetApi} from "../../client";
import {PersonalTyp, Umsatz} from "../../api";
import DataTable from "../data/DataTable";
import {TaetigkeitsberichtFilter} from "./TaetigkeitsberichtDataTable";
import useCustomFilters from "../data/useCustomFilters";
import StartEndFilter from "../StartEndFilter";
import {formatGermanNumber} from "../../utils/fomatters";
import {formatAsMoney} from "../../formatterHelpers";

export type TaetigkeitsberichtExternFilter = {
    search?: string;
    filtered?: string;
    startDate?: string;
    endDate?: string;
    isFremdleistung?: boolean;
};

export type Taetigkeitsbericht = {
    id: string;
    baustelleId: string;
    datum: string;
    taetigkeitsberichtTyp: PersonalTyp;
};

export type Props = Omit<DataRequest<TaetigkeitsberichtExternFilter>, "filter"> &
    Partial<Pick<DataRequest<TaetigkeitsberichtExternFilter>, "filter">>;

const tableName: string = "taetigkeitsberichte-extern"

export default function TaetigkeitsberichtDataTableExtern({...input}: Props) {
    const request = useDataRequest<TaetigkeitsberichtExternFilter>({
        filter: {isFremdleistung: true},
        ...input,
    });

    const [filteredState, setFilteredState] = useState("");

    return (
        <Stack spacing={2}>
            <FilterComp
                request={request}
                filteredStateFilter={filteredState}
            />
            <TaetigkeitsberichtResults
                request={request}
                setFilteredState={setFilteredState}
            />
        </Stack>
    );
}

function FilterComp({
                        request,
                        filteredStateFilter,
                    }: {
    request: DataRequestState<TaetigkeitsberichtExternFilter>;
    filteredStateFilter: string;
}) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<TaetigkeitsberichtExternFilter>({
        tableName: tableName,
        defaultFilter: {
            search: "",
            startDate: "",
            endDate: "",
            isFremdleistung: true,
        }
    });
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const {t: translation} = useT();

    useEffect(() => {
        setFilter({
            search: currentCustomFilter.search,
            startDate: currentCustomFilter.startDate,
            endDate: currentCustomFilter.endDate,
            isFremdleistung: currentCustomFilter.isFremdleistung,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: "100%",
            }}
        >
            {isMobile ? (
                <Stack direction="row">
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <StartEndFilter startDatum={currentCustomFilter.startDate}
                                    endeDatum={currentCustomFilter.endDate}
                                    setCustomField={setCustomField}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{marginLeft: 2}}
                        onClick={() => {
                            navigate("/neue-fremdleistung");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </Stack>
            ) : (
                <>
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <StartEndFilter startDatum={currentCustomFilter.startDate}
                                    endeDatum={currentCustomFilter.endDate}
                                    setCustomField={setCustomField}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{position: "absolute", top: 0, right: 0}}
                        onClick={() => {
                            navigate("/neue-fremdleistung");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </>
            )}
        </Box>
    );
}

function TaetigkeitsberichtResults({
                                       request,
                                       setFilteredState,
                                   }: {
    request: DataRequestState<TaetigkeitsberichtExternFilter>;
    setFilteredState: (value: string) => void;
}) {
    return <Results request={request} setFilteredState={setFilteredState}/>;
}

function Results({
                     request,
                     setFilteredState,
                 }: {
    request: DataRequestState<TaetigkeitsberichtExternFilter>;
    setFilteredState: (value: string) => void;
}) {
    const navigate = useNavigate();
    const getApi = useGetApi();

    const ladeUmsaetze: DataLoader<
        TaetigkeitsberichtFilter,
        Umsatz
    > = useCallback(
        async (params) =>
            (await getApi()).umsatz.sucheUmsatz({
                ...params,
            }),
        [getApi]
    );
    return (
        <DataTable
            columns={useColumns()}
            request={request}
            queryKey={[tableName, "umsaetze"]}
            loadData={ladeUmsaetze}
            onRowClick={(row: GridRowParams<Umsatz>, event) => {
                if (event.ctrlKey || event.metaKey) {
                    window.open(`${row.row.referenzUrl}`, "_blank");
                } else {
                    const currentSearch = window.location.search;
                    const params = new URLSearchParams();
                    if (currentSearch) {
                        params.set("returnSearch", currentSearch);
                    }
                    const query = params.toString();
                    navigate(`${row.row.referenzUrl}` + (query ? "?" + query : ""));
                }
            }}
            name={tableName}
            setFiltered={setFilteredState}
        />
    );
}

function useColumns(): Array<GridColDef<Umsatz>> {
    const {t: translation} = useT("vorgaenge");

    return useMemo(
        () => [
            {
                field: "buchungsDatum",
                headerName: capitalize(translation("date")),
                flex: 1,
                type: "date",
                valueGetter: (value) => {
                    return new Date(value);
                },
                renderCell: ({value}) =>
                    new Date(value as string).toLocaleDateString(undefined, {
                        month: "2-digit",
                        day: "2-digit",
                        year: "numeric",
                    }),
            },
            {
                field: "buchungsArt",
                headerName: capitalize(translation("booking-type")),
                flex: 1,
            },
            {
                field: "baustellenNummer",
                headerName: capitalize(translation("construction-site-number")),
                flex: 1,
                align: "right",
                headerAlign: "right",
            },
            {
                field: "baustellenBezeichnung",
                headerName: capitalize(translation("construction-site")),
                width: 250,
            },
            {
                field: "lieferantenBezeichnung",
                headerName: capitalize(translation("supplier")),
                flex: 1,
            },
            {
                field: "buchungsTyp",
                headerName: capitalize(translation("booking-short-type")),
                flex: 1,
            },
            {
                field: "vorgangBezeichnung",
                headerName: capitalize(translation("deponie-abladestelle")),
                flex: 2,
            },
            {
                field: "materialName",
                headerName: capitalize(translation("material")),
                flex: 2,
            },
            {
                field: "eakSchluessel",
                headerName: capitalize(translation("eak-nr")),
                flex: 1,
            },
            {
                field: "gesamtmenge",
                headerName: capitalize(translation("total-units")),
                align: "right",
                headerAlign: "right",
                flex: 1,
                type: "number",
                valueFormatter: formatGermanNumber,
            },
            {
                field: "mengeneinheit",
                headerName: capitalize(translation("unit")),
                flex: 1,
            },
            {
                field: "lieferscheinNr",
                headerName: capitalize(translation("delivery-note-number")),
                flex: 1,
            },
            {
                field: "betrag",
                headerName: capitalize(translation("total-amount")),
                flex: 1.5,
                align: "right",
                headerAlign: "right",
                type: "number",
                renderCell: ({value}) => {
                    return formatAsMoney(value);
                },
            },
            {
                field: "bemerkungTb",
                headerName: capitalize(translation("remark")),
                flex: 2,
            },
            {
                field: "bearbeitetUm",
                headerName: capitalize(translation("processing-time")),
                flex: 1.5,
                type: "date",
                valueGetter: (value) => {
                    return new Date(value);
                },
                renderCell: (params) => {
                    if (params.value) {
                        const date = new Date(params.value);
                        const formattedDate = date.toLocaleDateString(undefined, {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                        });
                        const formattedTime = date.toLocaleTimeString(undefined, {
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                        });
                        return `${formattedDate} ${formattedTime}`;
                    }
                    return "";
                },
            },
        ],
        [translation]
    );
}
